import * as Material from "@mui/material";

import "./styles.css";

import TitleBreadcrumb from '@components/TitleBreadcrumb';

import { useCommunity } from "contexts/community";
import Metric from "./components/Metric";
import Chart from "./components/Chart";
import { useEffect, useState } from "react";

import api from "@helpers/api.js";
import Select from "./components/Select";
import { InsightsRounded } from "@mui/icons-material";


export default function Communities() {
  document.title = "Dashboard | Ginastee";

  const [loading, setLoading] = useState(true);

  const { communities } = useCommunity();

  const [communitiesAvailable, setCommunitiesAvailable] = useState([]);

  const [community, setCommunity] = useState(
    communitiesAvailable[0]?.id || null
  );

  const [data, setData] = useState({
    subscribers: {
      total: 0,
      period_comparision: 0,

      history: {
        values: [
          {
            label: "Jan",
            value: 0,
          },
          {
            label: "Fev",
            value: 0,
          },
          {
            label: "Mar",
            value: 0,
          },
          {
            label: "Abr",
            value: 0,
          },
          {
            label: "Mai",
            value: 0,
          },
          {
            label: "Jun",
            value: 0,
          },
        ],
      },
    },

    receipt: {
      total: 0,
      period_comparision: 0,

      history: {
        granularity: "monthly",
        period: 6,
        mode: "liquid",
        values: [
          {
            label: "Jan",
            value: 0,
          },
          {
            label: "Fev",
            value: 0,
          },
          {
            label: "Mar",
            value: 0,
          },
          {
            label: "Abr",
            value: 0,
          },
          {
            label: "Mai",
            value: 0,
          },
          {
            label: "Jun",
            value: 0,
          },
        ],
      },
    },

    funnel: [
      { id: "page_visitor", value: 0, label: "Visitantes" },
      { id: "plan_chosen", value: 0, label: "Escolha do plano" },
      { id: "lead", value: 0, label: "Cadastros" },
      { id: "subscribe", value: 0, label: "Assinantes", active: true },
    ],

    mrr: 0,
    acl: 0,
    ltv: 0,
    chargeback: 0,
    churn: 0,
  });

  const [interval, setInterval] = useState("last_week");

  window.communities = {};

  useEffect(() => {
    const available = communities?.filter((c) => c.role === "creator") || [];

    setCommunitiesAvailable([...available]);
    setCommunity(available[0]?.id);
  }, [communities]);

  useEffect(() => {
    handleLoad();
  }, [community, interval]);

  async function handleLoad() {
    setLoading(true);

    try {
      const params = `interval=${interval}&community=${community}&granularity=daily`;
      const { data } = await api.get("insights/full?" + params);

      if (data) {
        data.funnel = data.funnel.map((f) => {
          if (f.id === "subscribe") f.active = true;
          return f;
        });
        setData({ ...data });
      }
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  }

  return (
    <div className="screen-body">
      <TitleBreadcrumb title={[
        {
          "title": "Relatórios",
          "url": "dashboard",
          iconLeft: <InsightsRounded color="white" />
        },
        {
          "title": "Geral"
        }
      ]} />
      <div>
          <div className="filters-top mt-1 mb-3">
            <div className="community-select-row mt-2 mb-1">
              <h4>Filtrar por intervalo:</h4>
              <Metric.OptionsSelect
                onChange={(v) => setInterval(v.target.value)}
                value={interval}
                options={[
                  {
                    label: "Última semana",
                    value: "last_week",
                  },
                  {
                    label: "Este mês",
                    value: "this_month",
                  },
                  {
                    label: "Mês passado",
                    value: "last_month",
                  },
                  {
                    label: "Este trimestre",
                    value: "this_quarter",
                  },
                  {
                    label: "Este ano",
                    value: "this_year",
                  },
                ]}
              />
            </div>

            <div className="community-select-row mt-2 mb-1">
              <h4>Comunidade: </h4>
              <Select
                options={communitiesAvailable.map((c) => ({
                  id: c.id,
                  label: c.name,
                }))}
                value={community}
                key={community}
                onChange={setCommunity}
              />
            </div>
          </div>

          <Metric.Row>
            <div
              style={{
                flex: 1,
                maxWidth: "380px",
              }}
            >
              <ActiveUsersAmount {...{ data, loading }} />
              <UsersGrowthHistory {...{ data, loading }} />
            </div>
            <Incomings
              key={loading}
              {...{ data, loading, community, interval }}
            />
          </Metric.Row>

          <Metric.Row>
            <Funnel {...{ data, loading }} />
          </Metric.Row>

          <Metric.Row>
            <MRR {...{ data, loading }} />
            <ACL {...{ data, loading }} />
            <LTV {...{ data, loading }} />
            <ChargeBack {...{ data, loading }} />
            <Churn {...{ data, loading }} />
          </Metric.Row>
      </div>
    </div>
  );
}

function ActiveUsersAmount({ data, loading }) {
  return (
    <Metric.Box
      className="mb-2"
      loading={loading}
      title="Assinantes ativos"
      type="main"
    >
      <Metric.Value
        size="large"
        type="number"
        value={data?.subscribers.total}
      />
      <Metric.Comparision
        value={data?.subscribers.period_comparision}
        type="white"
      />
    </Metric.Box>
  );
}

function Incomings({ data, loading, community, interval }) {
  const [mode, setMode] = useState("full");

  const [localLoading, setLocalLoading] = useState(loading);
  const [localData, setLocalData] = useState(data?.receipt);

  const [granularity, setGranularity] = useState(
    localData?.history.granularity || "monthly"
  );

  const revenue = localData?.history.values.map((v) => v[mode]);
  const labels = localData?.history.values.map((v) => v.label);

  async function handleLoad(info) {
    setLocalLoading(true);

    try {
      const params = `interval=${interval}&community=${community}&granularity=${info?.granularity || granularity
        }`;
      const { data } = await api.get("insights/receipts?" + params);

      if (data) {
        setLocalData({ ...data });
      }
    } catch (e) {
      console.log(e);
    }

    setLocalLoading(false);
  }

  function handleModeChange(v) {
    setMode(v);
  }
  function handleGranularityChange(v) {
    setGranularity(v);
    handleLoad({ granularity: v });
  }

  return (
    <Metric.Box
      loading={localLoading}
      minWidth={300}
      title="Faturamento"
      titleRight={
        <Metric.Comparision
          value={localData?.period_comparision}
          mode="percent"
        />
      }
    >
      <Metric.Row>
        <Metric.Value
          size="large"
          type="money"
          value={data.receipt.total[mode]}
        />
        <Metric.OptionsSelect
          onChange={(v) => handleModeChange(v.target.value)}
          value={mode}
          options={[
            {
              label: "Valor bruto",
              value: "full",
            },
            {
              label: "Valor líquido",
              value: "liquid",
            },
          ]}
          className="mb-2"
        />
      </Metric.Row>
      <div>
        <Metric.Row noWrap={true} center>
          <h4>Granularidade:</h4>

          <Select
            options={[
              {
                label: "Diária",
                id: "daily",
              },
              {
                label: "Semanal",
                id: "weekly",
              },
              {
                label: "Mensal",
                id: "monthly",
              },
              {
                label: "Trimestral",
                id: "quarterly",
              },
              {
                label: "Semestral",
                id: "biannually",
              },
              {
                label: "Anual",
                id: "yearly",
              },
            ]}
            value={granularity}
            onChange={handleGranularityChange}
          />
        </Metric.Row>
        <Chart.Line
          key={Math.random()}
          type="money"
          data={revenue}
          labels={labels}
        />
      </div>
    </Metric.Box>
  );
}

function UsersGrowthHistory({ data, loading }) {
  const revenue = data?.subscribers.history.values.map((v) => v.value);
  const labels = data?.subscribers.history.values.map((v) => v.label);

  return (
    <Metric.Box loading={loading} title="Base de assinantes no período">
      <Chart.Line height={200} data={revenue} labels={labels} />
    </Metric.Box>
  );
}
function Funnel({ data, loading }) {
  return (
    <Metric.Box loading={loading} centered title="Funil de aquisição">
      <Chart.Funnel data={data?.funnel} />
    </Metric.Box>
  );
}
function MRR({ data, loading }) {
  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title="MRR"
    >
      <Metric.Value type="money" value={data?.mrr.liquid} />
      <Metric.Info text="Receita líquida média mensal" />
    </Metric.Box>
  );
}
function ACL({ data, loading }) {
  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title="ACL"
    >
      <Metric.Value type="text" value={"" + data?.acl + " meses"} />
      <Metric.Info text="Tempo médio de permanência de um usuário antes de cancelar" />
    </Metric.Box>
  );
}
function LTV({ data, loading }) {
  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title="LTV"
    >
      <Metric.Value type="money" value={data?.ltv.liquid} />
      <Metric.Info text="Receita líquida média que um usuário transaciona na plataforma" />
    </Metric.Box>
  );
}
function ChargeBack({ data, loading }) {
  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title="Taxa de estorno"
    >
      <Metric.Value type="percent" value={data?.chargeback} />
      <Metric.Info text="Quantos % dos assinantes pedem estorno" />
    </Metric.Box>
  );
}
function Churn({ data, loading }) {
  return (
    <Metric.Box
      loading={loading}
      centered
      minWidth={180}
      maxWidth={250}
      title="Churn"
    >
      <Metric.Value type="percent" value={data?.churn} />
      <Metric.Info text="Quantos % dos assinantes cancelaram no período especificado" />
    </Metric.Box>
  );
}
